import { ArrowForward } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import type { FC } from 'react';

import pageUrls from 'constants/pageUrls';

import Button from 'components/Button';
import WarningOutlineIcon from 'components/Icons/WarningOutlineIcon';
import CustomAlert from 'components/MuiCustom/CustomAlert';
import StatusMessageBox from 'components/StatusMessageBox';

import { useAppSelector } from 'redux-store';

import type { AddLoadBalancingGroupModalStageProps } from '..';
import DeviceActivityStatus from './DeviceActivityStatus';

const AddDevicesToGroupsSuccess: FC<AddLoadBalancingGroupModalStageProps> = ({
  state,
  setState,
}) => {
  const { t } = useTranslation();
  const { selectedDevices, loadBalancingData, pendingDevices, selectedGroup } =
    state;
  const router = useRouter();

  const deviceList = useAppSelector(s => s.devices.items);

  const pendingDeviceMap = new Map(
    pendingDevices.map(device => [device.serial, device]),
  );

  const existingDevices = deviceList
    .filter(device => pendingDeviceMap.has(device.serial))
    .map(d => {
      const pendingDevice = pendingDeviceMap.get(d.serial);

      return {
        ...d,
        success: pendingDevice?.success,
      };
    });

  const handleRedirect = (): void => {
    if (!loadBalancingData?.loadBalancingGroupId) return;
    router.push(
      pageUrls.loadBalancingPage(loadBalancingData.loadBalancingGroupId),
    );
  };

  const handleOnClick = (serial: string): void => {
    setState(prev => ({
      ...prev,
      stage: 'protected',
      serial,
    }));
  };

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <StatusMessageBox
        type={pendingDevices.length > 0 ? 'warning' : 'success'}
        message={t(
          `common:pages.load_balancing.add_load_balancing_group.${
            pendingDevices.length > 0 ? 'pending' : 'success'
          }`,
        )}
        margin="0px 10px 10px 10px"
      />

      {pendingDevices.length > 0 ? (
        <CustomAlert
          severity="warning"
          icon={<WarningOutlineIcon sx={{ color: '#FFCC00' }} />}
          isWarningWithBackground
        >
          {t(
            'common:pages.load_balancing.add_load_balancing_group.pending_desc',
          )}
        </CustomAlert>
      ) : null}

      <Box mt={1} mb={1}>
        {existingDevices.map(device => (
          <DeviceActivityStatus
            key={device.serial}
            device={device}
            onClick={() => handleOnClick(device.serial)}
          />
        ))}
      </Box>
      {pendingDevices.length !== selectedDevices.length &&
      selectedGroup === null ? (
        <Box mt={2} display="flex" justifyContent="center">
          <Button
            variant="text"
            endIcon={<ArrowForward />}
            onClick={handleRedirect}
          >
            {t(
              'common:pages.load_balancing.add_load_balancing_group.go_to_group',
            )}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default AddDevicesToGroupsSuccess;
